import type {
  ScrapedContent,
  ViralContentAnalysisReport,
  ContentIdea,
  LibraryItem,
  UsageSummary,
  BrandProfileRecord,
  BrandProfile,
  BillingPlansResponse,
  AdminMember,
  AdminAccessRequest,
  AcademyCatalogResponse,
  AcademyCheckpoint,
  AcademyLessonSection,
  AcademyModule,
  AcademyToolHandoff,
  CaseStudy,
  CaseStudyEpisode,
  MemberAnnouncement,
  BotWorkspaceDetail,
  BotThread,
  BotMessage,
  BuilderMemory,
  ImplementationGuide,
  TemplateRepoAddonStatus,
  TemplateRepoProvisioningStatus,
  OSWorkspaceSnapshot,
  ProofSubmission,
  Testimonial,
  AdminOSSnapshot,
  AdminIntelligenceSnapshot,
  LinkedTelemetryDevice,
  MemberActivitySummary,
  WeeklyRecommendationReport,
} from '../domain/models';
import { apiKeyStorage } from '../utils/storage';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { runConvexMutation, runConvexQuery } from '../lib/convex';
import { resolveApiBase } from '../lib/apiBase';

const API_BASE = resolveApiBase(import.meta.env.VITE_API_BASE ?? import.meta.env.VITE_API_URL);

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    if (text) {
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch (parseError) {
        throw parseError instanceof Error ? parseError : new Error(text);
      }

      const message = parsed?.error ?? parsed?.message ?? text;
      const detailText = Array.isArray(parsed?.details)
        ? parsed.details
            .map((detail: unknown) => {
              if (!detail) return '';
              if (typeof detail === 'string') return detail;
              if (typeof detail === 'object') {
                const maybeMessage = (detail as { message?: unknown }).message;
                if (typeof maybeMessage === 'string') {
                  return maybeMessage;
                }
              }
              try {
                return JSON.stringify(detail);
              } catch {
                return String(detail);
              }
            })
            .filter((part: string) => Boolean(part))
            .join(' • ')
        : undefined;
      const combined =
        typeof message === 'string'
          ? detailText
            ? `${message}${message.includes(detailText) ? '' : `: ${detailText}`}`
            : message
          : response.statusText;
      throw new Error(combined || response.statusText);
    }
    throw new Error(response.statusText);
  }

  // 204 No Content or empty body → return undefined
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    // Non-JSON response; return as text
    const text = await response.text();
    return text as unknown as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }

  return JSON.parse(text) as T;
}

type AuthTokenProvider = () => Promise<string | null | undefined>;

let authTokenProvider: AuthTokenProvider | null = null;

export function configureApiAuth(provider: AuthTokenProvider) {
  authTokenProvider = provider;
}

async function getAuthToken() {
  return authTokenProvider ? await authTokenProvider() : undefined;
}

function shouldFallbackFromConvex(error: unknown) {
  if (!(error instanceof Error)) {
    return true;
  }

  const message = error.message?.toLowerCase() ?? '';
  return (
    message.includes('noauthprovider') ||
    message.includes('no auth provider') ||
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    message.includes('authentication') ||
    message.includes('token')
  );
}

async function tryConvexQuery<T>(name: string, args: Record<string, unknown>, token?: string | null) {
  if (!token) {
    return null;
  }

  try {
    return await runConvexQuery<T>(name, args, token ?? undefined);
  } catch (error) {
    if (shouldFallbackFromConvex(error)) {
      return null;
    }
    throw error;
  }
}

async function tryConvexMutation<T>(name: string, args: Record<string, unknown>, token?: string | null) {
  if (!token) {
    return null;
  }

  try {
    return await runConvexMutation<T>(name, args, token ?? undefined);
  } catch (error) {
    if (shouldFallbackFromConvex(error)) {
      return null;
    }
    throw error;
  }
}

async function authorizedRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = authTokenProvider ? await authTokenProvider() : undefined;
  const headers = new Headers(init.headers ?? {});

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Add API keys from localStorage as headers
  try {
    const { apifyToken, openaiToken } = apiKeyStorage.load();
    if (apifyToken) {
      headers.set('X-Apify-Token', apifyToken);
    }
    if (openaiToken) {
      headers.set('X-OpenAI-Token', openaiToken);
    }
  } catch (error) {
    // If localStorage is unavailable, continue without API keys in headers
    console.warn('Failed to load API keys from localStorage:', error);
  }

  const response = await fetchWithTimeout(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  return handleResponse(response);
}

export interface ScrapeRequest {
  platform: 'youtube' | 'instagram' | 'tiktok' | 'linkedin';
  actorId?: string;
  channelInputs: string[];
  maxResults: number;
  daysLookback: number;
  apifyToken?: string;
  openaiToken?: string;
}

export interface ScrapeResponse {
  sessionId: string;
  status: 'processing' | 'completed' | 'failed';
  items?: ScrapedContent[];
  message?: string;
}

export async function scrapeContent(payload: ScrapeRequest): Promise<ScrapeResponse> {
  let resolvedPayload = payload;

  try {
    const { apifyToken, openaiToken } = apiKeyStorage.load();
    resolvedPayload = {
      ...payload,
      apifyToken: payload.apifyToken ?? apifyToken ?? undefined,
      openaiToken: payload.openaiToken ?? openaiToken ?? undefined,
    };
  } catch (error) {
    console.warn('Unable to read API keys from local storage before scrape.', error);
  }

  if (!resolvedPayload.apifyToken || resolvedPayload.apifyToken.trim().length < 10) {
    throw new Error('Add your Apify API token in Manage API Keys before launching a scrape.');
  }

  const debugPayload = {
    ...resolvedPayload,
    apifyToken: resolvedPayload.apifyToken ? '***' : undefined,
    openaiToken: resolvedPayload.openaiToken ? '***' : undefined,
  };
  console.debug('[api] POST /scrape', {
    platform: debugPayload.platform,
    inputs: debugPayload.channelInputs?.length ?? 0,
    maxResults: debugPayload.maxResults,
    daysLookback: debugPayload.daysLookback,
    apifyKeyPresent: Boolean(resolvedPayload.apifyToken),
    openaiKeyPresent: Boolean(resolvedPayload.openaiToken),
  });

  return authorizedRequest('/scrape', {
    method: 'POST',
    body: JSON.stringify(resolvedPayload),
  });
}

export interface SessionStatusResponse {
  sessionId: string;
  status: 'processing' | 'completed' | 'failed';
  platform: string;
  items: ScrapedContent[];
  createdAt: string;
  error?: string;
}

export async function getScrapeSessionStatus(sessionId: string): Promise<SessionStatusResponse> {
  return authorizedRequest(`/scrape/sessions/${sessionId}`);
}

export interface AnalysisRequest {
  sessionId: string;
  apifyToken?: string;
  openaiToken?: string;
}

export async function generateAnalysisReport(
  payload: AnalysisRequest,
): Promise<ViralContentAnalysisReport> {
  return authorizedRequest('/analyze', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface IdeasRequest {
  sessionId: string;
  analysisId: string;
  openaiToken?: string;
}

export async function generateContentIdeas(
  payload: IdeasRequest,
): Promise<{ ideas: ContentIdea[] }> {
  return authorizedRequest('/generate-ideas', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// v2 unified ideas endpoint (no separate analysis step required)
export interface IdeasV2Response {
  analysis?: { summary?: string; patterns?: string[] };
  categories: Array<{
    name: string;
    ideas: Array<{
      id?: string;
      title: string;
      hook?: string;
      angle?: string;
      scriptOutline?: Array<{ beat: string; description: string }>;
      broll?: string[];
      cta?: string;
      tags?: string[];
      recommendedLength?: string;
      marker?: string;
      ideaType?: string;
      sourceRefId?: string | null;
    }>;
  }>;
}

export async function generateIdeasV2(payload: {
  sessionId: string;
  openaiToken?: string;
}): Promise<IdeasV2Response> {
  return authorizedRequest('/ideas-v2', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface AddToLibraryPayload {
  items: LibraryItem[];
}

export async function addToLibrary(payload: AddToLibraryPayload): Promise<void> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<{ status: string }>(
    'workflow:upsertLibraryItems',
    { items: payload.items },
    token ?? undefined,
  );
  if (convexResult) {
    return;
  }
  await authorizedRequest('/library/add', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchLibrary(): Promise<LibraryItem[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<LibraryItem[]>('workflow:listLibraryItems', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/library');
}

export async function fetchUsage(): Promise<UsageSummary> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<UsageSummary>('usage:getCurrent', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/usage');
}

export async function fetchBrandProfile(): Promise<BrandProfileRecord> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<BrandProfileRecord | null>(
    'brandProfiles:getCurrent',
    {},
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/brand-profile');
}

export async function updateBrandProfile(profile: BrandProfile): Promise<BrandProfileRecord> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<BrandProfileRecord>(
    'brandProfiles:upsertCurrent',
    { profile },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/brand-profile', {
    method: 'PUT',
    body: JSON.stringify({ profile }),
  });
}

export interface StoredApiKeys {
  apifyKey: string | null;
  openaiKey: string | null;
  updatedAt: string | null;
}

export async function fetchStoredApiKeys(): Promise<StoredApiKeys> {
  return authorizedRequest('/api-keys');
}

export async function updateStoredApiKeys(payload: { apifyKey: string; openaiKey: string }): Promise<StoredApiKeys> {
  return authorizedRequest('/api-keys', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteStoredApiKeys(): Promise<void> {
  await authorizedRequest('/api-keys', {
    method: 'DELETE',
  });
}

export interface TrackedCreator {
  id: string;
  name: string;
  platforms: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export async function listTrackedCreators(): Promise<TrackedCreator[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<TrackedCreator[]>('creators:listCurrent', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  const response = await authorizedRequest<{ creators: TrackedCreator[] }>('/creators');
  return response.creators ?? [];
}

export async function createTrackedCreator(payload: { name: string; platforms: Record<string, unknown> }): Promise<TrackedCreator> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<TrackedCreator>(
    'creators:createCurrent',
    { creatorId: crypto.randomUUID(), name: payload.name, platforms: payload.platforms },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/creators', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateTrackedCreator(creatorId: string, payload: { name?: string; platforms?: Record<string, unknown> }): Promise<TrackedCreator> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<TrackedCreator | null>(
    'creators:updateCurrent',
    { creatorId, ...payload },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/creators/${creatorId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteTrackedCreator(creatorId: string): Promise<void> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<{ status: string }>(
    'creators:deleteCurrent',
    { creatorId },
    token ?? undefined,
  );
  if (convexResult) {
    return;
  }
  await authorizedRequest(`/creators/${creatorId}`, {
    method: 'DELETE',
  });
}

export async function fetchBillingPlans(): Promise<BillingPlansResponse> {
  return authorizedRequest('/billing/plans');
}

export interface BillingCheckoutRequest {
  planId: string;
  cadence?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface BillingCheckoutResponse {
  url: string;
  id?: string;
}

export async function createBillingCheckoutSession(
  payload: BillingCheckoutRequest,
): Promise<BillingCheckoutResponse> {
  return authorizedRequest('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface BillingPortalResponse {
  url: string;
}

export async function createBillingPortalSession(returnUrl?: string): Promise<BillingPortalResponse> {
  return authorizedRequest('/billing/portal', {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

export interface AccessStatusResponse {
  userId: string | null;
  email: string | null;
  member: {
    email: string;
    status: string;
    updatedAt?: string;
    joinedAt?: string | null;
    skoolUserId?: string | null;
  } | null;
  accessCode: {
    code?: string;
    status: string;
    assignedEmail?: string | null;
    claimedAt?: string | null;
  } | null;
  allowed: boolean;
  planTier?: string | null;
  admin?: boolean;
}

export async function fetchAccessStatus(): Promise<AccessStatusResponse> {
  return authorizedRequest('/access/status');
}

export interface ClaimAccessCodePayload {
  code: string;
}

export type ClaimAccessCodeResponse = AccessStatusResponse & { status: string };

export async function claimAccessCode(payload: ClaimAccessCodePayload): Promise<ClaimAccessCodeResponse> {
  return authorizedRequest('/access/claim', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface AccessRequestPayload {
  message?: string;
}

export interface AccessRequestResponse {
  status: string;
}

export async function requestAccessInvite(payload: AccessRequestPayload): Promise<AccessRequestResponse> {
  return authorizedRequest('/access/request', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface ScrapeSessionSummary {
  id: string;
  platform: string;
  createdAt: string;
  itemCount: number;
}

export async function listScrapeSessions(): Promise<ScrapeSessionSummary[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<ScrapeSessionSummary[]>(
    'workflow:listScrapeSessions',
    {},
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/scrape-sessions');
}

export async function deleteScrapeSession(sessionId: string): Promise<void> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<{ status: string }>(
    'workflow:deleteScrapeSession',
    { sessionId },
    token ?? undefined,
  );
  if (convexResult) {
    return;
  }
  await authorizedRequest(`/scrape-sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

export interface ScrapeSessionData {
  id: string;
  userId: string;
  platform: string;
  createdAt: string;
  items: ScrapedContent[];
}

export async function getScrapeSession(sessionId: string): Promise<ScrapeSessionData> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<ScrapeSessionData | null>(
    'workflow:getScrapeSession',
    { sessionId },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  // Try the new endpoint first, fall back to old one for backward compatibility
  try {
    const response = await authorizedRequest<SessionStatusResponse>(`/scrape/sessions/${sessionId}`);
    return {
      id: response.sessionId,
      userId: '', // Not returned by new endpoint
      platform: response.platform,
      createdAt: response.createdAt,
      items: response.items,
    };
  } catch (error) {
    // Fall back to old endpoint
    return authorizedRequest(`/scrape-sessions/${sessionId}`);
  }
}

export async function fetchAdminMembers(): Promise<AdminMember[]> {
  const response = await authorizedRequest<{ members: AdminMember[] }>('/admin/members');
  return response.members ?? [];
}

export async function updateAdminMemberStatus(payload: { email: string; status: string }): Promise<AdminMember> {
  return authorizedRequest('/admin/members/status', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createAdminInviteCode(payload: { email?: string; note?: string }): Promise<{ code: string; assigned_email?: string | null }> {
  return authorizedRequest('/admin/access-codes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAdminAccessRequests(): Promise<AdminAccessRequest[]> {
  const response = await authorizedRequest<{ requests: AdminAccessRequest[] }>('/admin/access-requests');
  return response.requests ?? [];
}

export async function approveAccessRequestToken(token: string): Promise<{ status: string; email: string }> {
  return authorizedRequest('/admin/access-requests/approve', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function declineAccessRequestToken(token: string): Promise<{ status: string; email: string }> {
  return authorizedRequest('/admin/access-requests/decline', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function fetchOSWorkspaceSnapshot(): Promise<OSWorkspaceSnapshot> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<OSWorkspaceSnapshot>('os:getWorkspaceSnapshot', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/workspace');
}

export async function fetchBuilderMemory(): Promise<BuilderMemory> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<BuilderMemory>('os:getBuilderMemory', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/builder-memory');
}

export async function updateBuilderMemory(payload: Partial<BuilderMemory>): Promise<BuilderMemory> {
  const sanitizedPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== null),
  );
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<BuilderMemory>(
    'os:upsertBuilderMemory',
    sanitizedPayload,
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/builder-memory', {
    method: 'POST',
    body: JSON.stringify(sanitizedPayload),
  });
}

export async function fetchAcademyCatalog(): Promise<AcademyCatalogResponse> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<AcademyCatalogResponse>('os:getAcademyCatalog', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/academy/modules');
}

export async function fetchAcademyModule(moduleSlug: string): Promise<AcademyModule> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<AcademyModule>('os:getAcademyModule', { moduleSlug }, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/os/academy/modules/${moduleSlug}`);
}

export async function updateAcademyLessonProgress(payload: {
  moduleSlug: string;
  lessonSlug: string;
  completed: boolean;
}): Promise<AcademyModule> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<AcademyModule>(
    'os:updateAcademyLessonProgress',
    payload,
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/academy/progress', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchCaseStudy(): Promise<CaseStudy> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<CaseStudy>('os:getCaseStudy', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/case-study');
}

export async function fetchCaseStudyEpisode(episodeSlug: string): Promise<CaseStudyEpisode> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<CaseStudyEpisode>(
    'os:getCaseStudyEpisode',
    { episodeSlug },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/os/case-study/episodes/${episodeSlug}`);
}

export async function fetchAnnouncements(): Promise<MemberAnnouncement[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<MemberAnnouncement[]>('os:listAnnouncements', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  const response = await authorizedRequest<{ announcements: MemberAnnouncement[] }>('/os/announcements');
  return response.announcements ?? [];
}

export async function fetchProofSubmissions(): Promise<ProofSubmission[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<ProofSubmission[]>('os:listProofSubmissions', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  const response = await authorizedRequest<{ submissions: ProofSubmission[] }>('/os/proof');
  return response.submissions ?? [];
}

export async function createProofSubmission(payload: {
  builderName?: string;
  businessName?: string;
  title: string;
  summary: string;
  outcome: string;
}): Promise<ProofSubmission> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<ProofSubmission>('os:createProofSubmission', payload, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/proof', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function markAnnouncementAsRead(announcementId: string): Promise<{ status: string; announcementId: string }> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<{ status: string; announcementId: string }>(
    'os:markAnnouncementRead',
    { announcementId },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/os/announcements/${announcementId}/read`, {
    method: 'POST',
  });
}

export async function fetchBotWorkspace(workspaceSlug: string): Promise<BotWorkspaceDetail> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<BotWorkspaceDetail>(
    'os:getBotWorkspace',
    { workspaceSlug },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/os/bots/workspaces/${workspaceSlug}`);
}

export async function createBotThread(workspaceSlug: string, title?: string): Promise<BotThread> {
  const token = await getAuthToken();
  const convexResult = await tryConvexMutation<BotThread>(
    'os:createBotThread',
    { workspaceSlug, title },
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest(`/os/bots/workspaces/${workspaceSlug}/threads`, {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
}

export async function sendBotMessage(threadId: string, content: string): Promise<{ thread: BotThread; messages: BotMessage[] }> {
  return authorizedRequest(`/os/bots/threads/${threadId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function fetchImplementationGuides(): Promise<ImplementationGuide[]> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<ImplementationGuide[]>(
    'os:getImplementationGuides',
    {},
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  const response = await authorizedRequest<{ guides: ImplementationGuide[] }>('/os/guides');
  return response.guides ?? [];
}

export async function fetchTemplateRepoAddonStatus(): Promise<TemplateRepoAddonStatus> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<TemplateRepoAddonStatus>(
    'os:getTemplateRepoAddonStatus',
    {},
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/entitlements/template-repo-addon');
}

export async function fetchTemplateRepoProvisioningStatus(): Promise<TemplateRepoProvisioningStatus> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<TemplateRepoProvisioningStatus>(
    'os:getTemplateRepoProvisioningStatus',
    {},
    token ?? undefined,
  );
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/template-repo/provisioning');
}

export async function requestTemplateRepoProvisioning(payload: {
  notes?: string;
} = {}): Promise<TemplateRepoProvisioningStatus> {
  return authorizedRequest('/os/template-repo/provisioning', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAdminOSContent(): Promise<AdminOSSnapshot> {
  return authorizedRequest('/admin/os/content');
}

export async function fetchMemberActivitySummary(): Promise<MemberActivitySummary> {
  const token = await getAuthToken();
  const convexResult = await tryConvexQuery<MemberActivitySummary>('intelligence:getMemberActivitySummary', {}, token ?? undefined);
  if (convexResult) {
    return convexResult;
  }
  return authorizedRequest('/os/intelligence/activity');
}

export async function linkTelemetryDevice(payload: { deviceId: string; label?: string }): Promise<LinkedTelemetryDevice> {
  return authorizedRequest('/os/intelligence/devices/link', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchAdminIntelligence(periodDays = 30): Promise<AdminIntelligenceSnapshot> {
  return authorizedRequest(`/admin/os/intelligence?periodDays=${encodeURIComponent(String(periodDays))}`);
}

export async function generateWeeklyRecommendationReport(payload: {
  periodStart?: string;
  periodEnd?: string;
} = {}): Promise<WeeklyRecommendationReport> {
  return authorizedRequest('/admin/os/intelligence/reports/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createAdminAnnouncement(payload: {
  title: string;
  body: string;
  announcementType?: string;
  priority?: number;
  publishedAt?: string | null;
  expiresAt?: string | null;
  isActive?: boolean;
  ctaLabel?: string | null;
  ctaHref?: string | null;
}): Promise<Omit<MemberAnnouncement, 'isRead'>> {
  return authorizedRequest('/admin/os/announcements', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminTestimonial(
  testimonialId: string,
  payload: { isPublic?: boolean; isFeatured?: boolean },
): Promise<Testimonial> {
  return authorizedRequest(`/admin/os/testimonials/${testimonialId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminAcademyModule(
  moduleSlug: string,
  payload: { isPublished: boolean },
): Promise<{ id: string; slug: string; number: number; title: string; isPublished: boolean; lessonCount: number }> {
  return authorizedRequest(`/admin/os/modules/${moduleSlug}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminAcademyLesson(
  lessonSlug: string,
  payload: {
    title?: string;
    subtitle?: string | null;
    summary?: string;
    action?: string;
    communityPrompt?: string | null;
    toolSlug?: string | null;
    skillCommand?: string | null;
    assignmentPrompt?: string | null;
    conceptVideoUrl?: string | null;
    sections?: AcademyLessonSection[];
    checkpoints?: AcademyCheckpoint[];
    toolHandoffs?: AcademyToolHandoff[];
    pathSlugs?: string[];
  },
): Promise<{
  slug: string;
  title: string;
  subtitle?: string | null;
  summary: string;
  action: string;
  communityPrompt?: string | null;
  toolSlug?: string | null;
  skillCommand?: string | null;
  assignmentPrompt?: string | null;
  conceptVideoUrl?: string | null;
  sections?: AcademyLessonSection[];
  checkpoints?: AcademyCheckpoint[];
  toolHandoffs?: AcademyToolHandoff[];
  pathSlugs?: string[];
}> {
  return authorizedRequest(`/admin/os/lessons/${lessonSlug}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminAcademyResource(
  resourceId: string,
  payload: {
    title?: string;
    summary?: string;
    kind?: string;
    url?: string | null;
  },
): Promise<{
  id: string;
  title: string;
  summary: string;
  kind: string;
  url?: string | null;
}> {
  return authorizedRequest(`/admin/os/resources/${resourceId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminGuide(
  guideId: string,
  payload: {
    title?: string;
    subtitle?: string;
    estimatedTime?: string;
    topics?: string[];
    status?: 'available' | 'coming-soon';
  },
): Promise<ImplementationGuide> {
  return authorizedRequest(`/admin/os/guides/${guideId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminCaseStudyEpisode(
  episodeSlug: string,
  payload: {
    title?: string;
    summary?: string;
    whatWasBuilt?: string;
    deliverable?: string;
    docReference?: string | null;
    toolSlug?: string | null;
  },
): Promise<CaseStudyEpisode> {
  return authorizedRequest(`/admin/os/case-study/episodes/${episodeSlug}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminBotWorkspace(
  workspaceSlug: string,
  payload: { isActive: boolean },
): Promise<Omit<BotWorkspaceDetail, 'threads'> & { threadCount?: number }> {
  return authorizedRequest(`/admin/os/workspaces/${workspaceSlug}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function reviewAdminProofSubmission(
  submissionId: string,
  payload: { status: 'approved' | 'rejected'; adminNotes?: string },
): Promise<ProofSubmission> {
  return authorizedRequest(`/admin/os/proof/${submissionId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
